import { useState } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent, pickLocalized } from "@/contexts/SiteContentContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapPin, MessageCircle, Send, Loader2 } from "lucide-react";

const SERVICES = [
  { value: "starlink", de: "Starlink Internet", en: "Starlink Internet", es: "Internet Starlink" },
  { value: "wifi", de: "Premium WLAN & Mesh", en: "Premium WiFi & Mesh", es: "WiFi Premium & Mesh" },
  { value: "smart-home", de: "Smart Home & Sicherheit", en: "Smart home & security", es: "Smart home & seguridad" },
  { value: "digital", de: "Digitale Services", en: "Digital services", es: "Servicios digitales" },
  { value: "creator", de: "Creator & Streaming", en: "Creator & streaming", es: "Creator & streaming" },
  { value: "support", de: "Tech Support", en: "Tech support", es: "Soporte técnico" },
];

const Contact = () => {
  const { t, locale } = useLanguage();
  const { content } = useSiteContent();
  const contact = content?.contact || {};
  const phone = (contact.whatsapp_number || "+34657138915").replace(/[^+\d]/g, "");
  const waUrl = `https://wa.me/${phone.replace(/^\+/, "")}`;
  const location = pickLocalized(contact.location, locale, "Mallorca · Inselweiter Service");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(locale === "de" ? "Bitte alle Pflichtfelder ausfüllen." : "Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        locale,
        service: form.service || null,
        phone: form.phone || null,
        source_domain: typeof window !== "undefined" ? window.location.hostname : null,
      });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#f7f2ea] py-24 lg:py-32"
    >
      <div className="container-px mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left panel */}
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] text-sky-700 font-medium">
              {t("contact.eyebrow")}
            </p>
            <h2 className="font-display mt-3 text-4xl lg:text-5xl font-light tracking-tighter text-neutral-950 text-balance">
              {t("contact.title")}
            </h2>
            <p className="mt-5 text-neutral-700 leading-relaxed">
              {t("contact.subtitle")}
            </p>

            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-btn"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-neutral-950 text-white px-6 py-4 text-sm font-medium hover:bg-black transition-colors"
            >
              <MessageCircle className="w-4 h-4 text-emerald-400" />
              {t("contact.whatsapp")}
            </a>

            <div className="mt-10 rounded-3xl overflow-hidden border border-neutral-200 bg-white">
              <div className="relative aspect-[16/10]">
                <iframe
                  title="Mallorca"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=2.0%2C39.2%2C3.6%2C40.0&layer=mapnik"
                  className="absolute inset-0 w-full h-full grayscale-[40%]"
                  loading="lazy"
                />
              </div>
              <div className="p-5 flex items-center gap-3">
                <MapPin className="w-4 h-4 text-sky-700" />
                <div className="text-sm text-neutral-700">{location}</div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-3xl bg-white border border-neutral-200 p-7 lg:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name">{t("contact.name")} *</Label>
                <Input
                  id="name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  required
                  className="rounded-xl bg-white border-neutral-200 h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t("contact.email")} *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  required
                  className="rounded-xl bg-white border-neutral-200 h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t("contact.phone")}</Label>
                <Input
                  id="phone"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="rounded-xl bg-white border-neutral-200 h-12"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("contact.service")}</Label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger
                    data-testid="contact-service-select"
                    className="rounded-xl bg-white border-neutral-200 h-12"
                  >
                    <SelectValue placeholder={t("contact.service.placeholder")} />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s[locale] || s.de}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="message">{t("contact.message")} *</Label>
                <Textarea
                  id="message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  required
                  rows={5}
                  className="rounded-xl bg-white border-neutral-200"
                />
              </div>
            </div>

            <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
              <div className="text-xs text-neutral-500">
                {locale === "de"
                  ? "Antwort meist innerhalb 24 Stunden."
                  : locale === "es"
                  ? "Respuesta normalmente en 24 horas."
                  : "We usually reply within 24 hours."}
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-7 py-3.5 text-sm font-medium hover:bg-black disabled:opacity-60 transition-colors"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {t("contact.submit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
